import React, {useCallback, useEffect, useState} from "react";
import AppBar from "@material-ui/core/AppBar";
import {
    ExpanderTable, ExpanderTableContent,
    ExpanderTableHeader,
    ExpanderTablePager, ExpanderTableRows,
    HeaderToolbar,
} from "../../components";
import {makeStyles} from "@material-ui/core/styles";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Menu from "@material-ui/core/Menu";
import {fetchAccount} from "../../store/actions";
import {useStore} from "../../store";
import {ErrorScreen} from "../../components";
import {Loading} from "../../components";
import {InvoiceStatus} from "../../components";
import {animateScroll} from 'react-scroll'
import format from "../../utils/format";
import {InvoiceService} from "../../services";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton"
import MoreVert from "@material-ui/icons/MoreVert";
import Refresh from "@material-ui/icons/Refresh";
import Button from "@material-ui/core/Button";
import externalLink from "../../utils/external-link";
import FuseAnimate from "../../components/fuse-animate/fuse-animate";
import purchases from "../../utils/purchases";

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
    },
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
    },
    content: {
        display       : 'flex',
        flexDirection : 'column',
        flexGrow      : 1,
        marginTop    : theme.spacing(2),
    },
    dataLabel: {
        minWidth: 75,
        fontWeight: 500,
    },
}));


const PurchasesPage = ({location}) => {
    const classes = useStyles();
    const [store, dispatch] = useStore();   // Get the global store
    const [state, setState] = useState({
        isFetching : false,
        error      : null,
        charges    : [],    // Charge data
        total      : 0,     // total number of calls
        filtered   : 0,     // total number of calls after filtering
        page       : 0,     // current page (numbered from 0)
        pageLength : 10,    // number of records to display per page
        pageCount  : 0,     // total number of pages available
        scrollTop  : false, // If set, scroll to the top of the page after fetching
        showLoader : false, // if true display the table loading indicator
    });
    const [menuAnchor, setMenuAnchor] = useState(null)
    const tableRef = React.createRef();

    // Scroll to top on mount
    useEffect( () => {
        document.getElementById('psb').scrollTo(0,0);
    }, [])  // <- empty deps means it runs only on load

    // Make sure the account is available in the store
    useEffect(()=> {
        if (store.user.accountId) {
            dispatch(fetchAccount(store.user.accountId, store, dispatch))
        }
    }, [store.user.accountId, store, dispatch])

    // fetchData is a callback that returns row data for the ExpanderTable
    //
    // When the table requires data it calls this function passing four parameters:
    //  - page:        the page number (first page is zero)
    //  - pageLength:  the maximum number of rows to return
    //  - sorting:     the expected sort columns and order of the result set.
    //  - search:      the user's search term if any
    //
    // The callback should return an object:
    // {
    //     rows:     array - the result rows (or an empty array)
    //     total:    int   - the total number of rows available
    //     filtered: int   - the total number of rows returned after applying the search term.
    // }
    //
    // The function should be memoized using useCallback otherwise a new function will be passed to the
    // table each render which will cause the table to request the data each time it renders.
    const fetchData = useCallback((page, pageLength, sorting={}) => {
        return new Promise((resolve, reject) => {

            // If we don't have an accountId yet, we can't proceed...
            if (store.account.id === 0) {
                resolve({total: 0, filtered: 0, rows: []})
                return
            }

            // Set the sort order
            const sort = sorting.column && sorting.direction ? sorting.column+','+sorting.direction : 'date,desc';

            InvoiceService.search(store.account.id, page * pageLength, pageLength, sort)
                .then(result => {
                    resolve({
                        total    : result.total,
                        filtered : result.filtered,
                        rows     : purchases(result.invoices),  // convert invoices to purchases
                    })
                })
                .catch(error => {
                    setState(s =>({...s, isFetching: false, error}))
                })

        })
    }, [store.account.id])

    const handlePageVisibility = (isVisible, viewportOffset) => {
        // If the page controls at the bottom of the table are offscreen and above
        // the viewport, scroll to the top
        if (!isVisible && viewportOffset < 0) {
            animateScroll.scrollToTop({containerId: 'psb'});
        }
    }

    const handleMenuButtonClick = (event) => {
        // Open the menu at the
        setMenuAnchor(event.currentTarget)
    }
    const handleMenuItemClick = (event) => {
        if (event.target.id === 'refresh' && tableRef.current) {
            tableRef.current.reload();
        }
        setMenuAnchor(null);
    }
    const handleMenuClose = (event) => {
        setMenuAnchor(null);
    }

    return(
        <div className={classes.root}>
            <AppBar position="fixed" className={classes.appBar}>
                <HeaderToolbar location={location.pathname}/>
            </AppBar>
            <Toolbar />

            <FuseAnimate animation="transition.expandIn" delay={200}>
                <main className={classes.content}>
                    { store.account.error && <div className="my-auto pb-24"><ErrorScreen error={store.account.error}/></div> }
                    { state.error && <div className="my-auto pb-24"><ErrorScreen error={state.error}/></div> }
                    { store.account.isFetching ? <div className="my-auto pb-24"><Loading/></div> : null }
                    { store.account.error === null && state.error === null && !store.account.isFetching ?
                        <div className="flex flex-col flex-grow">
                            <Typography variant="h5" color="secondary">Purchases</Typography>

                            <ExpanderTable
                                onData={fetchData}
                                ref={tableRef}
                                columns={[
                                    {field: 'date',      title: 'Date',      style: 'w-5/12 md:w-1/4'},
                                    {field: 'reference', title: 'Reference', style: 'hidden md:flex md:w-1/4'},
                                    {field: 'amount',    title: 'Amount',    style: 'w-4/12 md:w-1/4'},
                                    {field: 'status',    title: 'Status',    style: 'w-3/12 md:flex md:w-1/4'}
                                ]}>

                                <ExpanderTableHeader className="pt-4 pb-2 pl-4 pr-1 uppercase" >
                                    <div style={{marginTop:-12, marginBottom: -10}}>
                                        <IconButton onClick={handleMenuButtonClick}>
                                            <MoreVert/>
                                        </IconButton>
                                        <Menu
                                            anchorEl={menuAnchor}
                                            keepMounted
                                            open={Boolean(menuAnchor)}
                                            onClose={handleMenuClose}
                                        >
                                            <MenuItem
                                                id="refresh"
                                                onClick={handleMenuItemClick}
                                            >
                                                <Refresh className="mr-2"/>
                                                Refresh
                                            </MenuItem>
                                        </Menu>
                                    </div>
                                </ExpanderTableHeader>

                                <ExpanderTableContent noData="No purchases to show.">
                                    <ExpanderTableRows
                                        rowKey="id"
                                        SummaryComponent={PurchaseSummary}
                                        ContentComponent={PurchaseContent}
                                    />
                                </ExpanderTableContent>

                                <ExpanderTablePager onVisibilityChange={handlePageVisibility}/>
                            </ExpanderTable>
                        </div>
                        : null
                    }
                </main>
            </FuseAnimate>
        </div>
    )

}

// PurchaseSummary renders the header for a row in the ExpanderTable
const PurchaseSummary = ({row}) => {
    return (
        <div className="flex flex-row flex-grow font-light items-center">
            <div className="w-5/12 md:w-1/4">{format('date-only', row.date)}</div>
            <div className="hidden md:flex md:w-1/4">{row.reference}</div>
            <div className="w-4/12 md:w-1/4 font-bold">${((row.amount - row.amountRefunded)/100).toFixed(2)}</div>
            <div className="w-3/12 md:flex md:w-1/4">
                <InvoiceStatus status={row.status} />
            </div>
        </div>
    )
}

// PurchaseContent renders the content in the expander in the ExpanderTable
const PurchaseContent = ({row}) => {
    const classes = {
        dataLabel: {
            minWidth: 75,
            fontWeight: 500,
        },
    };

    return (
        <div className="flex flex-col flex-grow font-light">
            <div className="flex flex-col sm:flex-row">
                <div className="flex flex-row p-1 sm:w-1/2 pb-3">
                    <div style={classes.dataLabel}>Date</div>
                    <div className="">{format('date-long',row.date)}</div>
                </div>
                <div className="flex flex-row p-1 sm:w-1/2 pb-3">
                    <div style={classes.dataLabel}>Amount</div>
                    <div className="">${(row.amount/100).toFixed(2)}</div>
                </div>
            </div>
                <div className="flex flex-col sm:flex-row">
                    <div className="flex flex-row p-1 sm:w-1/2 pb-3">
                        <div style={classes.dataLabel}>Item</div>
                        <div className="">{row.description}</div>
                    </div>
                { row.amountRefunded > 0 ?
                    <div className="flex flex-row p-1 sm:w-1/2 pb-3">
                        <div style={classes.dataLabel}>Refunded</div>
                        <div className="">${(row.amountRefunded/100).toFixed(2)}</div>
                    </div>
                    : null
                }
                </div>
            <div className="flex flex-col sm:flex-row">
                <div className="flex flex-row p-1 sm:w-1/2 pb-3">
                    <div style={classes.dataLabel}>Reference</div>
                    <div className="">{row.reference}</div>
                </div>
                <div className="flex flex-row p-1 sm:w-1/2 pb-3">
                    <div style={classes.dataLabel}>Status</div>
                    <div className="capitalize">{row.status}</div>
                </div>
            </div>

            <Button variant="outlined"
                    color="primary"
                    size="large"
                    onClick={(e) => externalLink(e, row.receiptUrl, true)}
            >View Receipt</Button>
        </div>
    )
}


export default PurchasesPage;